import { ChevronsDownUpIcon, CircleCheckIcon, CircleDashedIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";

import { classNames as cn } from "@calcom/lib";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import useMeQuery from "@calcom/trpc/react/hooks/useMeQuery";

const POPOVER_ID = "onboarding_popover";

export function OnboardingCard() {
  const { t } = useLocale();
  const me = useMeQuery();
  const { data: onboardingState } = trpc.viewer.profile.getOnboardingState.useQuery();

  if (!me.data || me.data.userType !== "COACH" || me.data.coachProfile) {
    return null;
  }

  const total = me.data.isReferralOnly ? 5 : 6;

  return (
    <>
      <button
        // @ts-expect-error `popovertarget` is not yet in the React types
        popovertarget={POPOVER_ID}
        className="group/card -mx-1 mb-3 hidden flex-row items-start gap-2 rounded-md border border-gray-200 bg-white p-2 text-left text-sm shadow-sm transition hover:shadow-md lg:flex">
        <CircularProgress
          total={total}
          progress={onboardingState ? Object.values(onboardingState).filter((v) => v === true).length : 0}
        />

        <div>
          <h2 className="font-cal text-sm font-medium">{t("lb_getting_started")}</h2>
          <p className="text-xs text-gray-500">{t("lb_getting_started_subtitle")}</p>
        </div>
      </button>
    </>
  );
}

export function OnboardingPopover() {
  const { t } = useLocale();
  const router = useRouter();
  const me = useMeQuery();
  const { data: onboardingState } = trpc.viewer.profile.getOnboardingState.useQuery();

  if (!me.data || me.data.userType !== "COACH" || me.data.coachProfile) {
    return null;
  }

  const isReferralOnly = !!me.data.isReferralOnly;
  const total = isReferralOnly ? 5 : 6;

  return (
    <dialog
      id={POPOVER_ID}
      // @ts-expect-error `popover` is not yet in the React types
      popover="manual"
      className="border-brand-600 shadow-brand-600/50 hover:shadow-brand-600/50 bottom-3 left-3 mb-0 ml-0 mr-3 rounded-md border bg-white p-0 shadow-sm transition hover:shadow-lg">
      <div className="relative flex flex-col gap-2 px-2 py-4 md:gap-4 md:px-4">
        <button
          // @ts-expect-error `popovertarget` is not yet in the React types
          popovertarget={POPOVER_ID}
          popovertargetaction="close"
          className="absolute right-1 top-1 p-3 text-gray-400 hover:text-gray-600">
          <ChevronsDownUpIcon className="h-4 w-4" />
        </button>

        <div className="flex flex-row items-center gap-3">
          <CircularProgress
            total={total}
            progress={onboardingState ? Object.values(onboardingState).filter((v) => v === true).length : 0}
          />

          <div className="pr-8">
            <h2 className="font-cal text-base font-medium">{t("lb_getting_started")}</h2>
            <p className="text-sm text-gray-500">{t("lb_getting_started_subtitle")}</p>
          </div>
        </div>

        <ul className="flex flex-col gap-1 px-2 text-sm text-gray-500 md:gap-2 md:text-base">
          <Li href="/profile/information" checked={onboardingState?.profile}>
            {t("lb_complete_profile")}
          </Li>

          {!onboardingState?.profile && router.asPath === "/profile/information" ? (
            <div className="ml-[7px] flex flex-col gap-1 border-l-2 border-gray-100 pl-4">
              <Li checked={onboardingState?.profileBreakdown.name}>{t("lb_complete_profile_name")}</Li>
              <Li checked={onboardingState?.profileBreakdown.avatar}>{t("lb_complete_profile_avatar")}</Li>
              <Li checked={onboardingState?.profileBreakdown.about}>{t("lb_complete_profile_bio")}</Li>
              <Li checked={onboardingState?.profileBreakdown.specializations}>
                {t("lb_complete_profile_specializations")}
              </Li>
            </div>
          ) : null}

          <>
            <Li href="/profile/certificates" checked={onboardingState?.certificate}>
              {t("lb_item_add_certificates")}
            </Li>
            <Li href="/dashboard" checked={onboardingState?.calendar}>
              {t("lb_item_connect_calendar")}
            </Li>
            {!isReferralOnly ? (
              <Li href="/dashboard" checked={onboardingState?.stripe}>
                {t("lb_item_connect_stripe")}
              </Li>
            ) : null}
            <Li href="/profile/services" checked={onboardingState?.services}>
              {t("lb_item_create_services")}
            </Li>
            <Li href="/dashboard" checked={(me.data?.coachProfileDraft?.reviewStatus ?? "DRAFT") !== "DRAFT"}>
              {t("lb_item_publish_profile")}
            </Li>
          </>
        </ul>
      </div>
    </dialog>
  );
}

function Li({ checked, href, children }: { checked?: boolean; href?: string; children?: React.ReactNode }) {
  return (
    <li className="flex flex-row items-center gap-2">
      {checked ? (
        <CircleCheckIcon className="text-brand-600 h-4 w-4 shrink-0" />
      ) : (
        <CircleDashedIcon className="h-4 w-4 shrink-0 text-gray-400" />
      )}
      {!href ? (
        <span>{children}</span>
      ) : (
        <Link href={href} className="text-gray-500 transition-colors hover:text-gray-900">
          {children}
        </Link>
      )}
    </li>
  );
}

function CircularProgress({ total, progress }: { total: number; progress: number }) {
  return (
    <div
      className="relative isolate flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
      style={
        {
          "--v": `${(360 - 5 * total) /* 5deg padding between segments */ / total}deg`,
        } as React.CSSProperties
      }>
      <style>
        {`
          .arc {
            aspect-ratio: 1;
            clip-path: shape(from top,
                arc to calc(50% + 50% * sin(var(--v))) 
                calc(50% - 50% * cos(var(--v))) of 50% large cw,
                line to center);
          }
        `}
      </style>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "arc absolute left-0 top-0 h-full w-full origin-center rounded-full border-[3px]",
            i < (progress ?? 0) ? "border-brand-600" : "border-gray-200"
          )}
          style={{ transform: `rotate(${(360 / total) * i + 2.5}deg)` }}
        />
      ))}
      <div className="relative z-10 text-xs font-medium text-gray-700">
        {progress}/{total}
      </div>
    </div>
  );
}
